import { Alert, DatePicker, Form, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import type { ReceiptActionTarget } from '../hooks/use-receipt-action';
import { RECEIPT_ACTION_LABELS, type ReceiptAction } from '../policies/receipt-actions';

const DATE_FORMAT = 'YYYY-MM-DD';

const EXPLANATIONS: Record<ReceiptAction, string> = {
  clear: 'The bank has paid the cheque. The receipt stays as recorded; this adds the date it cleared.',
  bounce: 'The bank returned the cheque unpaid. The receipt stays in the history with your reason.',
  reverse: 'Cancels this payment, for example when it was recorded by mistake. The receipt stays in the history with your reason.',
};

interface ReceiptActionModalProps {
  /** What is being done to which receipt; null keeps the dialog closed. */
  target: ReceiptActionTarget | null;
  error: string | null;
  submitting: boolean;
  /** The cleared date (`YYYY-MM-DD`) for Mark cleared, the reason for the others. Resolves to whether it saved. */
  onSubmit: (value: string) => Promise<boolean>;
  onClose: () => void;
}

/** A receipt is evidence, so bouncing or reversing it records why; clearing records the day. A refusal from the server shows at the top and the dialog stays open. */
export function ReceiptActionModal({ target, error, submitting, onSubmit, onClose }: ReceiptActionModalProps) {
  const [form] = Form.useForm<{ value: string }>();
  const action = target?.action;

  async function submit({ value }: { value: string }) {
    if (await onSubmit(value.trim())) onClose();
  }

  return (
    <Modal
      open={target !== null}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={action ? RECEIPT_ACTION_LABELS[action] : undefined}
      cancelText="Cancel"
      okButtonProps={{ danger: action !== 'clear', loading: submitting }}
      title={action ? `${RECEIPT_ACTION_LABELS[action]}?` : undefined}
      destroyOnHidden
    >
      {action && <p className="mt-0 text-muted">{EXPLANATIONS[action]}</p>}
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        {action === 'clear' ? (
          <Form.Item
            name="value"
            label="Cleared on"
            rules={[{ required: true, message: 'Enter the date the cheque cleared' }]}
            getValueProps={(value?: string) => ({ value: value ? dayjs(value) : undefined })}
            normalize={(day?: dayjs.Dayjs) => day?.format(DATE_FORMAT)}
          >
            <DatePicker className="w-full" disabledDate={(day) => day.isAfter(dayjs(), 'day')} />
          </Form.Item>
        ) : (
          <Form.Item name="value" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
